/** Skip compression for files already small enough. */
export const IMAGE_COMPRESSION_BYPASS_BYTES = 1.5 * 1024 * 1024;

/** Default long-edge limit for standard uploads. */
export const IMAGE_COMPRESSION_MAX_DIMENSION = 1600;

/** Long-edge limit when high-quality mode is enabled. */
export const IMAGE_COMPRESSION_HIGH_QUALITY_MAX_DIMENSION = 1920;

/** Target upper bound for compressed output. */
export const IMAGE_COMPRESSION_TARGET_MAX_BYTES = 1.2 * 1024 * 1024;

export const IMAGE_COMPRESSION_WEBP_QUALITY = 0.78;
export const IMAGE_COMPRESSION_JPEG_QUALITY = 0.8;

export interface ImageCompressionOptions {
  highQuality?: boolean;
}

export interface ImageCompressionResult {
  file: File;
  wasCompressed: boolean;
  originalSize: number;
  compressedSize: number;
}

interface LoadedImage {
  source: CanvasImageSource;
  width: number;
  height: number;
  cleanup: () => void;
}

function getMaxDimension(options?: ImageCompressionOptions): number {
  return options?.highQuality
    ? IMAGE_COMPRESSION_HIGH_QUALITY_MAX_DIMENSION
    : IMAGE_COMPRESSION_MAX_DIMENSION;
}

function getOutputFileName(originalName: string, mimeType: string): string {
  const baseName = originalName.replace(/\.[^.]+$/, '') || 'image';
  const extension = mimeType === 'image/webp' ? 'webp' : 'jpg';
  return `${baseName}.${extension}`;
}

function scaleDimensions(
  width: number,
  height: number,
  maxDimension: number,
): { width: number; height: number } {
  const longEdge = Math.max(width, height);
  if (longEdge <= maxDimension) {
    return { width, height };
  }
  const ratio = maxDimension / longEdge;
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

async function loadImageSource(file: File): Promise<LoadedImage> {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file);
    return {
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      cleanup: () => bitmap.close(),
    };
  }
  const objectUrl = URL.createObjectURL(file);
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Impossible de charger l\'image.'));
    img.src = objectUrl;
  });
  return {
    source: image,
    width: image.naturalWidth,
    height: image.naturalHeight,
    cleanup: () => URL.revokeObjectURL(objectUrl),
  };
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('La compression de l\'image a échoué.'));
          return;
        }
        resolve(blob);
      },
      mimeType,
      quality,
    );
  });
}

function supportsWebpEncoding(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }
  const canvas = document.createElement('canvas');
  return canvas.toDataURL('image/webp').startsWith('data:image/webp');
}

async function encodeCanvas(
  canvas: HTMLCanvasElement,
  originalName: string,
  quality: number,
): Promise<File> {
  const useWebp = supportsWebpEncoding();
  const mimeType = useWebp ? 'image/webp' : 'image/jpeg';
  const blob = await canvasToBlob(canvas, mimeType, quality);
  return new File([blob], getOutputFileName(originalName, mimeType), {
    type: mimeType,
    lastModified: Date.now(),
  });
}

async function renderCompressedFile(
  file: File,
  maxDimension: number,
  quality: number,
): Promise<File> {
  const loadedImage = await loadImageSource(file);
  try {
    const { width, height } = scaleDimensions(
      loadedImage.width,
      loadedImage.height,
      maxDimension,
    );
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Impossible de préparer la compression de l\'image.');
    }
    context.drawImage(loadedImage.source, 0, 0, width, height);
    return encodeCanvas(canvas, file.name, quality);
  } finally {
    loadedImage.cleanup();
  }
}

export function shouldCompressImage(
  file: File,
  options?: ImageCompressionOptions,
): boolean {
  if (options?.highQuality) {
    return file.size > IMAGE_COMPRESSION_BYPASS_BYTES;
  }
  return file.size >= IMAGE_COMPRESSION_BYPASS_BYTES;
}

export async function compressImageForUpload(
  file: File,
  options?: ImageCompressionOptions,
): Promise<ImageCompressionResult> {
  const originalSize = file.size;
  if (!shouldCompressImage(file, options)) {
    return {
      file,
      wasCompressed: false,
      originalSize,
      compressedSize: originalSize,
    };
  }
  const maxDimension = getMaxDimension(options);
  const qualities = options?.highQuality
    ? [IMAGE_COMPRESSION_WEBP_QUALITY, 0.72, 0.66]
    : [IMAGE_COMPRESSION_WEBP_QUALITY, 0.7, 0.62, 0.55];
  let currentMaxDimension = maxDimension;
  let bestFile: File | null = null;
  for (let dimensionStep = 0; dimensionStep < 2; dimensionStep += 1) {
    for (const quality of qualities) {
      const candidate = await renderCompressedFile(file, currentMaxDimension, quality);
      bestFile = candidate;
      if (candidate.size <= IMAGE_COMPRESSION_TARGET_MAX_BYTES) {
        return {
          file: candidate,
          wasCompressed: true,
          originalSize,
          compressedSize: candidate.size,
        };
      }
    }
    currentMaxDimension = Math.round(currentMaxDimension * 0.85);
  }
  if (!bestFile) {
    return {
      file,
      wasCompressed: false,
      originalSize,
      compressedSize: originalSize,
    };
  }
  return {
    file: bestFile,
    wasCompressed: true,
    originalSize,
    compressedSize: bestFile.size,
  };
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024))} Ko`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}
